/**
 * Long-term lead follow-up ("still interested, check back next January").
 *
 * Stored on the lead's account doc as one top-level map, written whole:
 *   longTermFollowUp: { month: 'YYYY-MM', date: 'YYYY-MM-DD' | null, note, updatedAt, updatedBy }
 * `month` is always set; `date` is an optional exact day inside that month.
 */

export const FOLLOW_UP_FIELD = 'longTermFollowUp';

const MONTH_RE = /^(\d{4})-(\d{2})$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const toMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const toDateKey = (date) =>
  `${toMonthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;

/** 'YYYY-MM' → 'Jan 2027' (short) or 'January 2027'. */
export function formatFollowUpMonth(monthKey, { short = true } = {}) {
  const m = MONTH_RE.exec(monthKey || '');
  if (!m) return '';
  const name = MONTH_NAMES[Number(m[2]) - 1] || '';
  return `${short ? name.slice(0, 3) : name} ${m[1]}`;
}

/**
 * The lead's follow-up, normalized — or null when none is set.
 * { month: 'YYYY-MM', year, monthIndex (0–11), date: Date | null, note, label, overdue }
 */
export function getLeadFollowUp(account, now = new Date()) {
  const raw = account?.[FOLLOW_UP_FIELD];
  if (!raw || typeof raw !== 'object') return null;
  const dateMatch = DATE_RE.exec(raw.date || '');
  const date = dateMatch ? new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3])) : null;
  const month = MONTH_RE.test(raw.month || '') ? raw.month : date ? toMonthKey(date) : null;
  if (!month) return null;
  const [, y, mo] = MONTH_RE.exec(month);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const overdue = date ? date < today : month < toMonthKey(now);
  return {
    month,
    year: Number(y),
    monthIndex: Number(mo) - 1,
    date,
    note: typeof raw.note === 'string' ? raw.note.trim() : '',
    label: date
      ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
      : formatFollowUpMonth(month),
    overdue,
  };
}
